import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { ExternalLink, UserPlus, KeyRound, Link2, RefreshCw, Zap } from "lucide-react";
import { DHAN_REFERRAL_URL } from "@/components/BuyViaDhan";

const STEPS = [
  {
    icon: UserPlus,
    title: "1 · Open a DHAN account (free)",
    body: "ProTradeIQ executes trades through your own DHAN account. If you don't have one yet, open it in ~10 minutes — fully online with PAN + Aadhaar.",
    cta: { label: "Open DHAN account", href: DHAN_REFERRAL_URL },
    testid: "guide-step-account",
  },
  {
    icon: KeyRound,
    title: "2 · Whitelist ProTradeIQ's server IP",
    body: "SEBI's algo-trading rules require API orders to come from a whitelisted static IP. In web.dhan.co → My Profile → DhanHQ APIs → Whitelisted IPs, add the server IP shown in ProTradeIQ's Connect DHAN window (you can whitelist up to 5 IPs). Do this BEFORE generating your token.",
    cta: { label: "Open web.dhan.co", href: "https://web.dhan.co/" },
    testid: "guide-step-whitelist",
  },
  {
    icon: KeyRound,
    title: "3 · Generate your API access token",
    body: "Same page: DhanHQ Trading APIs → Generate Access Token. Copy your Client ID and the token. Tokens are valid for 24h (or as configured), so you'll refresh it each trading day. Always generate the token AFTER whitelisting the IP — old tokens stay tied to the old IP.",
    testid: "guide-step-token",
  },
  {
    icon: Link2,
    title: "4 · Connect inside ProTradeIQ",
    body: "On the Dashboard, open any 'Buy via DHAN' button (or the Options Lab strategy cards) → Connect DHAN account → paste your Client ID + Access Token. We verify them live with DHAN before saving.",
    testid: "guide-step-connect",
  },
  {
    icon: Zap,
    title: "5 · Execute with one click",
    body: "Every AI signal shows a Best Strike & Premium Plan card with the exact suggested entry price. Hit BUY via DHAN, pick your lots, confirm — the order lands in your DHAN account at the suggested limit price.",
    testid: "guide-step-execute",
  },
  {
    icon: RefreshCw,
    title: "6 · Daily token refresh",
    body: "DHAN tokens expire daily. If a button says 'token expired', repeat step 2 and reconnect — takes under a minute.",
    testid: "guide-step-refresh",
  },
];

export default function DhanGuidePage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar variant="marketing" />
      <div className="pt-28 pb-20 px-6 md:px-12 max-w-4xl mx-auto">
        <div className="text-xs font-mono tracking-[0.3em] text-[#007AFF] mb-4 uppercase">◢ Broker integration</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-black uppercase" data-testid="dhan-guide-heading">
          How to connect DHAN
        </h1>
        <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
          ProTradeIQ is a DHAN-only execution environment for Indian markets. Your credentials stay in your account —
          we only relay the orders you explicitly confirm.
        </p>

        <a
          href={DHAN_REFERRAL_URL} target="_blank" rel="noreferrer"
          className="mt-8 inline-flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wider py-3.5 px-7 transition-colors"
          data-testid="dhan-referral-cta"
        >
          <ExternalLink className="h-4 w-4" /> New to DHAN? Open a free account
        </a>

        <div className="mt-14 space-y-px bg-zinc-900">
          {STEPS.map((s) => (
            <div key={s.title} className="bg-[#0A0A0A] p-6 md:p-8 hover:bg-[#101010] transition-colors flex gap-5" data-testid={s.testid}>
              <s.icon className="h-6 w-6 text-[#007AFF] shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-lg font-bold uppercase tracking-tight">{s.title}</h2>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                {s.cta && (
                  <a href={s.cta.href} target="_blank" rel="noreferrer"
                     className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#3B9BFF] hover:underline font-mono uppercase tracking-widest">
                    <ExternalLink className="h-3 w-3" /> {s.cta.label}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-[10px] text-zinc-600 font-mono uppercase tracking-widest leading-relaxed">
          Credentials encrypted at rest · orders route only to your own DHAN account · derivatives trading involves risk — SEBI risk disclosures apply
        </div>

        <Link to="/dashboard" className="mt-8 inline-block text-sm text-[#007AFF] hover:underline font-mono uppercase tracking-widest" data-testid="guide-back-dashboard">
          ← Back to dashboard
        </Link>
      </div>
    </div>
  );
}
